import { useState, useEffect } from "react";

/* ─── Styles ─── */
import { getGlobalStyles } from "./styles/global";
import { btn3d } from "./styles/buttonStyles";

/* ─── Constants ─── */
import { DAY_COLORS, DAY_ACCENT } from "./constants/colors";

/* ─── Services ─── */
import { fbDb, fbAuth, getIdToken, FUNCTIONS_BASE, doc, deleteDoc } from "./services/firebase";

/* ─── UI Components ─── */
import { StepDots } from "./components/ui";
import { GeneratingProgress } from "./components/GeneratingProgress";
import AppHeader from "./components/AppHeader";
import BottomNav from "./components/BottomNav";
import ErrorBoundary from "./components/ErrorBoundary";
import ConsentGate from "./components/ConsentGate";

/* ─── Hooks ─── */
import { useSettings } from "./hooks/useSettings";
import { useAuth } from "./hooks/useAuth";
import { useItinerary } from "./hooks/useItinerary";
import { useTourAPI } from "./hooks/useTourAPI";
import { useReviews } from "./hooks/useReviews";
import { useCommunity } from "./hooks/useCommunity";
import { useRanking, normalizeRankingEntry } from "./hooks/useRanking";

/* ─── Pages ─── */
import IntroPage from "./pages/IntroPage";
import PersonalPage from "./pages/PersonalPage";
import TravelPage from "./pages/TravelPage";
import ResultPage from "./pages/ResultPage";
import ReviewPage from "./pages/ReviewPage";
import MyTripPage from "./pages/MyTripPage";
import CommunityPage from "./pages/CommunityPage";
import RankingPage from "./pages/RankingPage";
import MyInfoPage from "./pages/MyInfoPage";

/* ─── Responsive Typography + Reset ───
   clamp(min, preferred(vw), max): 작은 폰~데스크톱까지 폰트가 유동적으로
   스케일되되 양끝에서 고정된다. font-size만 지정 — 배치는 각 요소 몫. */
function ResponsiveStyle() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; }
      html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
      body { margin: 0; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }

      .text-xs   { font-size: clamp(0.6875rem, 2.8vw, 0.75rem); }   /* ~11–12px */
      .text-sm   { font-size: clamp(0.8125rem, 3.3vw, 0.875rem); }  /* ~13–14px */
      .text-base { font-size: clamp(0.9375rem, 3.9vw, 1rem); }      /* ~15–16px */
      .text-md   { font-size: clamp(1rem,      4.3vw, 1.1875rem); } /* ~16–19px */
      .text-lg   { font-size: clamp(1.125rem,  5vw,   1.375rem); }  /* ~18–22px */
      .text-xl   { font-size: clamp(1.375rem,  6vw,   1.625rem); }  /* ~22–26px */
      .text-2xl  { font-size: clamp(1.625rem,  7.5vw, 2rem); }      /* ~26–32px */
    `}</style>
  );
}

/* 일정 생성 플로우에 속하는 화면 — 여기서만 StepDots 노출 */
const FLOW_STEPS = ["personal", "travel", "generating", "result"];

export default function TravelApp() {
  /* ── Settings (darkMode / lang / companion) ── */
  const settings = useSettings();
  const { darkMode, setDarkMode, lang, setLang, dm, t, companionType } = settings;

  /* ── Navigation + profile/travel state (parent-owned) ── */
  const [step, setStep] = useState("intro");
  const [view, setView] = useState("itinerary"); // "itinerary" | "map"
  const [personal, setPersonal] = useState({ name:"", age:"", gender:"", bloodType:"", mbti:"", zodiac:"", address:"", email:"" });
  const [travel, setTravel] = useState({ startDate:"", endDate:"", departure:"", destination:"", departureTime:"", style:"", transport:"" });
  const [sharedTripIds, setSharedTripIds] = useState([]);
  const [showDailyCard, setShowDailyCard] = useState(false);
  const [personalError, setPersonalError] = useState("");
  const [travelError2, setTravelError2] = useState("");

  /* ── Auth (현재 personal을 캡처해 인자 없는 프로필 저장 API 제공) ── */
  const auth = useAuth({ personal, setPersonal });
  const { firebaseUid, loggedInUser, profileRequired } = auth;

  const updatePersonal = (k, v) => { setPersonal(p => ({ ...p, [k]: v })); setPersonalError(""); };
  const updateTravel = (k, v) => { setTravel(tv => ({ ...tv, [k]: v })); setTravelError2(""); };
  const travelNights = (travel.startDate && travel.endDate)
    ? Math.round((new Date(travel.endDate) - new Date(travel.startDate)) / 864e5) : 0;

  /* ── Community + Ranking (before itinerary: provide setCommunityTrips / setRankingData) ── */
  const comm = useCommunity(fbDb);
  const rank = useRanking(fbDb, firebaseUid);

  /* ── Ranking upsert: parent-owned dependency injected into useItinerary ── */
  const upsertRanking = async (_reviewsOverride = null, incrementPlan = false) => {
    if (!personal.name) return;
    const uid = firebaseUid || fbAuth.currentUser?.uid;
    if (!uid) return;
    try {
      const idToken = await getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/upsertRanking`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ incrementPlan }),
      });
      if (!res.ok) { console.warn("랭킹 업데이트 실패:", res.status); return; }
      const { entry } = await res.json();
      if (!entry) return;
      const normalizedEntry = normalizeRankingEntry(entry);
      rank.setRankingData(prev => {
        const current = (Array.isArray(prev) ? prev : []).map((item, index) => normalizeRankingEntry(item, item?.id || `ranking-${index}`));
        const others = current.filter(u => u.name !== personal.name);
        const board = [normalizedEntry, ...others];
        board.sort((a, b) => (b.reviewCount*2 + b.badges.length*3 + b.totalLikes) - (a.reviewCount*2 + a.badges.length*3 + a.totalLikes));
        return board;
      });
    } catch (e) { console.error("랭킹 업데이트 오류:", e); }
  };

  /* ── Itinerary (generation / chat / PDF / checklist / save / share) ── */
  const itin = useItinerary({
    personal, travel, travelNights,
    darkMode, lang, t, companionType,
    fbDb, fbAuth, firebaseUid, loggedInUser,
    hasRequiredConsents: auth.hasRequiredConsents,
    requestRequiredConsent: auth.requestRequiredConsent,
    setStep, setView, setPersonal, setTravel, upsertRanking,
    setCommunityTrips: comm.setCommunityTrips, setSharedTripIds,
  });

  /* ── TourAPI + Reviews ── */
  const tour = useTourAPI();
  const rev = useReviews(fbDb, fbAuth, firebaseUid);

  /* ── Review persistence helper (legacy compat) ── */
  const saveReviews = async (newList) => { rev.setReviews(newList); };

  /* ── Global styles injection ── */
  useEffect(() => {
    let el = document.getElementById("tc-global-styles");
    if (!el) {
      el = document.createElement("style");
      el.id = "tc-global-styles";
      document.head.appendChild(el);
    }
    el.textContent = getGlobalStyles(dm);
  }, [dm]);

  /* ── 페이지 전환 시 스크롤 최상단 복귀 ── */
  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  /* ── 신규 Google 회원 / 프로필 저장 실패 계정의 가입 마무리 ── */
  useEffect(() => {
    if (profileRequired) setStep("personal");
  }, [profileRequired]);

  /* ── Google 재인증 redirect 복귀: 탈퇴가 자동 재개되므로 진행 화면으로 데려간다 ──
     복귀 시 새 마운트라 step은 "intro"다. 그대로 두면 되돌릴 수 없는 삭제가
     인트로 화면 뒤에서 아무 표시 없이 돈다("돌아오면 자동으로 이어집니다" 안내와 어긋난다). */
  useEffect(() => {
    if (auth.deletionResumedFromRedirect) setStep("myinfo");
  }, [auth.deletionResumedFromRedirect]);

  /* ── Shared prop context for pages ──
     Spread order matters:
       - comm before itin  → itin.shareToCommunity (current itinerary) wins for ResultPage
       - tour after itin   → useTourAPI tour data wins (the dedicated source) */
  const ctx = {
    ...settings,
    ...auth,
    ...rev,
    ...rank,
    ...comm,
    ...itin,
    ...tour,
    // parent-owned state + handlers
    step, setStep, view, setView,
    personal, setPersonal, travel, setTravel, travelNights,
    updatePersonal, updateTravel,
    sharedTripIds, setSharedTripIds,
    showDailyCard, setShowDailyCard,
    personalError, setPersonalError,
    travelError2, setTravelError2,
    // glue
    upsertRanking, saveReviews,
    // constants + services used directly by pages
    DAY_COLORS, DAY_ACCENT,
    fbDb, fbAuth, doc, deleteDoc,
  };

  /* ── Active page ── */
  const renderPage = () => {
    if (profileRequired && step !== "personal") return <PersonalPage {...ctx} />;

    switch (step) {
      case "personal":     return <PersonalPage {...ctx} />;
      case "travel":       return <TravelPage {...ctx} />;
      case "generating":   return <GeneratingProgress progress={itin.progress} darkMode={dm} t={t} />;
      case "result":       return <ResultPage {...ctx} />;
      case "review":       return <ReviewPage {...ctx} />;
      case "mytrip":       return <MyTripPage {...ctx} />;
      case "community":
      case "reviews_feed": return <CommunityPage {...ctx} />;
      case "ranking":      return <RankingPage {...ctx} />;
      case "myinfo":       return <MyInfoPage {...ctx} />;
      case "intro":
      case "login":
      default:             return <IntroPage {...ctx} />;
    }
  };

  /* ── 하단 탭 내비게이션 ── */
  const handleNav = (tabKey) => {
    if (tabKey === "intro") { setStep("intro"); return; }
    if (tabKey === "myinfo") {
      if (loggedInUser) { setStep("myinfo"); }
      else { auth.setAuthMode("login"); setStep("login"); }
      return;
    }
    setStep(tabKey);
  };

  return (
    <>
      <ResponsiveStyle />
      <div className="app-shell">
        <AppHeader
          dm={dm} lang={lang} setLang={setLang}
          darkMode={darkMode} setDarkMode={setDarkMode}
          onLogo={() => setStep("intro")}
        />
        {FLOW_STEPS.includes(step) && (
          <StepDots current={step} darkMode={dm} lang={lang} onStepClick={(s) => setStep(s)} />
        )}
        {auth.profileLoadError && (
          <div role="alert" className="text-sm" style={{ marginBottom:12, padding:"10px 14px", borderRadius:10, background:dm?"rgba(255,90,80,0.12)":"rgba(255,230,227,0.85)", border:"1px solid rgba(200,60,45,0.35)", fontWeight:700, color:dm?"#FF8F80":"#C0392B", textAlign:"center" }}>
            ⚠️ {auth.profileLoadError}
          </div>
        )}
        {rev.reviewsLoadError && ["review", "community", "reviews_feed"].includes(step) && (
          <div role="alert" className="text-sm" style={{ marginBottom:12, padding:"10px 14px", borderRadius:10, background:dm?"rgba(255,90,80,0.12)":"rgba(255,230,227,0.85)", border:"1px solid rgba(200,60,45,0.35)", fontWeight:700, color:dm?"#FF8F80":"#C0392B", textAlign:"center" }}>
            <div style={{ marginBottom:8 }}>⚠️ {rev.reviewsLoadError}</div>
            <button onClick={rev.loadReviews} className="btn3d text-xs" style={{ ...btn3d("danger", dm), padding:"7px 13px", minHeight:36, borderRadius:10, fontWeight:800 }}>
              다시 불러오기
            </button>
          </div>
        )}
        <ErrorBoundary resetKey={step} dm={dm} onGoHome={() => setStep("intro")}>
          {renderPage()}
        </ErrorBoundary>
      </div>
      {step !== "generating" && (
        <BottomNav step={step} lang={lang} onNav={handleNav} />
      )}
      <ConsentGate
        dm={dm}
        consentGate={auth.consentGate}
        privacyAgreed={auth.privacyAgreed}
        overseasTransferAgreed={auth.overseasTransferAgreed}
        confirmConsentGate={auth.confirmConsentGate}
        closeConsentGate={auth.closeConsentGate}
      />
    </>
  );
}
